import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import QuoteProductDimentionsUomSelector from '@views/components/Selectors/QuoteProductDimentionsUomSelector';
import { ClassSelect, WeightUomSelect } from '@views/screens/common';
import { QuoteProductClassSelect } from '@views/screens/Quote/components/selects';
import { Col, Row } from 'antd';
import { routeFileSelector } from '@utilz/i18n';

import { FormSchema } from '@/modules/validators';
import { calculateCube, calculateExtVolume, calculateExtWeight } from '@/utils/measures';

import FilterCard from '../../../../../../components/Cards/FilterCard';
import FormInput from '../../../../../../components/Inputs/FormInput';
import FormTextArea from '../../../../../../components/Inputs/FormTextArea';
import HiddenFormInput from '../../../../../../components/Inputs/HiddenFormInput';
import FormLabel from '../../../../../../components/Labels/FormLabel';
import SecondaryLabel from '../../../../../../components/Labels/SecondaryLabel';
import SaveCancelButtons from '../../../SaveCancelButtons';

const HallProductDetailsCard = ({
  index,
  name,
  itemDetails = {},
  remove,
  handleDataChange,
  isLast = false,
  dataTestId,
}) => {
  const editMode = Boolean(itemDetails.editMode);

  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);

  const cube = useMemo(
    () => calculateCube(itemDetails?.length, itemDetails?.width, itemDetails?.height, 1, itemDetails?.dimUom),
    [itemDetails?.length, itemDetails?.width, itemDetails?.height, itemDetails?.dimUom],
  );

  const extWeightValue = useMemo(
    () => calculateExtWeight(itemDetails?.quantity, itemDetails?.weight),
    [itemDetails?.quantity, itemDetails?.weight],
  );

  const extVolume = useMemo(
    () => calculateExtVolume(itemDetails?.quantity, itemDetails?.cube),
    [itemDetails?.quantity, itemDetails?.cube],
  );

  useEffect(() => {
    if (cube) {
      handleDataChange({ cube }, index);
    }
  }, [cube, handleDataChange, index]);

  useEffect(() => {
    handleDataChange({ itemSequenceKey: index + 1 }, index);
  }, [index, handleDataChange]);

  return (
    <FilterCard
      labelForTestId={dataTestId}
      shadow={false}
      showBorder
      className={isLast ? '' : 'mb-15'}
      customHeader={
        <div className="w-100">
          <Row justify="space-between" align="middle">
            <Col sm={6}>
              <Row align="middle" gutter={15}>
                <Col className="flex-1">
                  <FormSchema.Field name={`quoteDetails.${index}.productCode`}>
                    {({ antdRule, isRequired }) => (
                      <FormInput
                        name={[name, 'productCode']}
                        label={t('translation:getQuote.productDetails.productCode.label')}
                        margin="m-0"
                        editMode={editMode}
                        singleLineMode
                        colon
                        rules={[antdRule]}
                        isRequired={isRequired}
                        dataTestId="prod-details-card-code"
                      />
                    )}
                  </FormSchema.Field>
                </Col>
              </Row>
            </Col>
            <Col>
              <SaveCancelButtons handleRemove={() => remove(name)} editMode={false} hideEditButton />
            </Col>
          </Row>
        </div>
      }
    >
      <div className="p-15">
        <Row gutter={20}>
          <Col sm={4}>
            <FormLabel className="f-14 fw-700">{t('translation:default.fields.productDesc.label')}</FormLabel>
            <FormSchema.Field name={`quoteDetails.${index}.productDescription`}>
              {({ antdRule, isRequired }) => (
                <FormTextArea
                  editMode={editMode}
                  margin={!editMode ? 'mb-12' : 'mb-20'}
                  name={[name, 'productDescription']}
                  rows={editMode ? 4 : 3}
                  isRequired={isRequired}
                  rules={[antdRule]}
                  dataTestId="prod-details-card-description"
                />
              )}
            </FormSchema.Field>
          </Col>

          <Col sm={11}>
            <Col>
              <Row gutter={15}>
                <Col sm={7}>
                  <FormSchema.Field name={`quoteDetails.${index}.quantity`}>
                    {({ antdRule, isRequired, label }) => (
                      <FormInput
                        name={[name, 'quantity']}
                        label={label}
                        margin="m-0"
                        editMode={editMode}
                        inputType="number"
                        isRequired={isRequired}
                        rules={[antdRule]}
                        dataTestId="prod-details-card-quantity"
                      />
                    )}
                  </FormSchema.Field>
                </Col>

                <Col sm={9}>
                  <FormSchema.Field name={`quoteDetails.${index}.weight`}>
                    {({ antdRule, isRequired, label }) => (
                      <FormInput
                        name={[name, 'weight']}
                        label={label}
                        margin="m-0"
                        editMode={editMode}
                        inputType="number"
                        placeholder={t('translation:default.fields.weight')}
                        isRequired={isRequired}
                        rules={[antdRule]}
                        dataTestId="prod-details-card-weight"
                      />
                    )}
                  </FormSchema.Field>
                </Col>

                <Col sm={8}>
                  <FormSchema.Field name={`quoteDetails.${index}.weightUom`}>
                    {({ antdRule, isRequired, label }) => (
                      <WeightUomSelect
                        name={[name, 'weightUom']}
                        label={label}
                        margin="m-0"
                        editMode={editMode}
                        placeholder={t('translation:getQuote.productDetails.weightUom.placeholder')}
                        onChange={(val) => handleDataChange({ weightUomName: val?.label }, index)}
                        isRequired={isRequired}
                        rules={[antdRule]}
                      />
                    )}
                  </FormSchema.Field>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col sm={5}>
                  <FormSchema.Field name={`quoteDetails.${index}.length`}>
                    {({ antdRule, isRequired, label }) => (
                      <FormInput
                        name={[name, 'length']}
                        label={label}
                        margin="m-0"
                        editMode={editMode}
                        inputType="number"
                        placeholder="00"
                        isRequired={isRequired}
                        rules={[antdRule]}
                        dataTestId="prod-details-card-length"
                      />
                    )}
                  </FormSchema.Field>
                </Col>
                <Col sm={5}>
                  <FormSchema.Field name={`quoteDetails.${index}.width`}>
                    {({ antdRule, isRequired, label }) => (
                      <FormInput
                        name={[name, 'width']}
                        label={label}
                        margin="m-0"
                        editMode={editMode}
                        inputType="number"
                        placeholder="00"
                        rules={[antdRule]}
                        isRequired={isRequired}
                        dataTestId="prod-details-card-width"
                      />
                    )}
                  </FormSchema.Field>
                </Col>
                <Col sm={4}>
                  <FormSchema.Field name={`quoteDetails.${index}.height`}>
                    {({ antdRule, isRequired, label }) => (
                      <FormInput
                        name={[name, 'height']}
                        label={label}
                        margin="m-0"
                        editMode={editMode}
                        inputType="number"
                        placeholder="00"
                        isRequired={isRequired}
                        rules={[antdRule]}
                        dataTestId="prod-details-card-height"
                      />
                    )}
                  </FormSchema.Field>
                </Col>
                <Col sm={5}>
                  <FormSchema.Field name={`quoteDetails.${index}.dimUom`}>
                    {({ antdRule, isRequired, label }) => (
                      <QuoteProductDimentionsUomSelector
                        name={[name, 'dimUom']}
                        label={label}
                        margin="m-0"
                        editMode={editMode}
                        placeholder={t('translation:getQuote.productDetails.dimUom.placeholder')}
                        onChange={(val) => handleDataChange({ dimUomName: val?.label }, index)}
                        isRequired={isRequired}
                        rules={[antdRule]}
                      />
                    )}
                  </FormSchema.Field>
                </Col>

                <Col sm={5}>
                  <FormSchema.Field name={`quoteDetails.${index}.cube`}>
                    {({ antdRule, isRequired, label }) => (
                      <FormInput
                        name={[name, 'cube']}
                        label={label}
                        margin="m-0"
                        editMode={editMode}
                        inputType="number"
                        placeholder="00"
                        min={0}
                        isRequired={isRequired}
                        rules={[antdRule]}
                        dataTestId="prod-details-card-cube"
                      />
                    )}
                  </FormSchema.Field>
                </Col>
              </Row>
            </Col>
          </Col>

          <Col sm={2}>
            <Row gutter={8}>
              <Col data-testid="product-details-ext-weight">
                <FormLabel className="f-14 fw-700">
                  {t('translation:getQuote.productDetails.extWeight.label')}
                </FormLabel>
                <br />
                <SecondaryLabel className="f-14 fw-600 text-dark">{extWeightValue}</SecondaryLabel>
              </Col>
            </Row>

            <Row gutter={10} className="mt-20">
              <Col data-testid="product-details-ext-volume">
                <FormLabel className="f-14 fw-700">
                  {t('translation:getQuote.productDetails.extVolume.label')}
                </FormLabel>
                <br />
                <SecondaryLabel className="f-14 fw-600 text-dark">{extVolume}</SecondaryLabel>
              </Col>
            </Row>
          </Col>

          <Col sm={3}>
            <Row gutter={15}>
              <Col sm={24}>
                <FormSchema.Field name={`quoteDetails.${index}.hazardousClass`}>
                  {({ antdRule, isRequired, label }) => (
                    <QuoteProductClassSelect
                      name={[name, 'hazardousClass']}
                      label={label}
                      placeholder={t('translation:getQuote.productDetails.hazardousClass.placeholder')}
                      margin="m-0"
                      editMode={editMode}
                      onChange={(val) => handleDataChange({ className: val?.label }, index)}
                      dataTestId="prod-details-card-hazardous-class"
                      isRequired={isRequired}
                      rules={[antdRule]}
                    />
                  )}
                </FormSchema.Field>
              </Col>
            </Row>
            <Row gutter={20}>
              <Col sm={24}>
                <FormSchema.Field name={`quoteDetails.${index}.class`}>
                  {({ antdRule, isRequired, label }) => (
                    <ClassSelect
                      name={[name, 'class']}
                      label={label}
                      margin="m-0"
                      editMode={editMode}
                      isRequired={isRequired}
                      rules={[antdRule]}
                      dataTestId="prod-details-card-class"
                    />
                  )}
                </FormSchema.Field>
              </Col>
            </Row>
          </Col>

          <Col sm={4}>
            <Row gutter={15}>
              <Col sm={24}>
                <FormSchema.Field name={`quoteDetails.${index}.harmonizedCode1`}>
                  {({ antdRule, isRequired, label }) => (
                    <FormInput
                      name={[name, 'harmonizedCode1']}
                      label={label}
                      margin="m-0"
                      editMode={editMode}
                      initialValues="500"
                      dataTestId="prod-details-card-harmonized-code-1"
                      isRequired={isRequired}
                      rules={[antdRule]}
                    />
                  )}
                </FormSchema.Field>
              </Col>
            </Row>

            <Row gutter={20}>
              <Col sm={24}>
                <FormSchema.Field name={`quoteDetails.${index}.harmonizedCode2`}>
                  {({ antdRule, isRequired, label }) => (
                    <FormInput
                      name={[name, 'harmonizedCode2']}
                      label={label}
                      margin="m-0"
                      editMode={editMode}
                      dataTestId="prod-details-card-harmonized-code-2"
                      isRequired={isRequired}
                      rules={[antdRule]}
                    />
                  )}
                </FormSchema.Field>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
      <HiddenFormInput name={[name, 'editMode']} />
      <HiddenFormInput name={[name, 'isNew']} />
      <HiddenFormInput name={[name, 'itemSequenceKey']} />
      <HiddenFormInput name={[name, 'gridOperation']} />
      <HiddenFormInput name={[name, 'createdBy']} />
      <HiddenFormInput name={[name, 'billQtyUomName']} />
      <HiddenFormInput name={[name, 'className']} />
      <HiddenFormInput name={[name, 'dimUomName']} />
      <HiddenFormInput name={[name, 'weightUomName']} />
    </FilterCard>
  );
};
export default HallProductDetailsCard;
